import React, { useMemo, useRef, useCallback, useEffect } from 'react'

import { SitecoreTranslations, withParsedContext } from '../../withJSSContext'
import useSWRInfinite, { SWRInfiniteKeyLoader } from 'swr/infinite'

import { GenericTile } from '@christies/react/components/Tiles'
import { Checkbox, LoadMore, ShowMore } from '@christies/react/components/ButtonsInputs'

import { fetcher, ParsedStoriesResponse, Result } from './fetcher'
import { track, TrackingEvents } from '@christies/react/utils'
import { TFunction } from 'i18next'
import { Endpoint } from '../../dataFetcher'
import { useSearchFilters, SelectedFilter } from './useSearchFilters'
import { ErrorBoundary as Error } from '@christies/react/components/Atoms'
import { FilterTag, StoriesArchive } from '@christies/react/dist/lib/components/Shared'
import { ReactComponent } from '@christies/react/typings'

const getSWRKey = ({ parameters, ...endpoint }: Endpoint): SWRInfiniteKeyLoader => {
  return (pageIndex: number, previousPageData: ParsedStoriesResponse): ReturnType<SWRInfiniteKeyLoader> => {
    const max = Number(parameters?.aggMax) ?? 1000
    const defaultCount = Number(parameters?.count) ?? 48
    if (previousPageData && !previousPageData.results.length) return null // reached the end

    const offset = pageIndex * defaultCount
    const willBeFetched = offset + defaultCount

    const count = willBeFetched > max ? max - offset : defaultCount

    return {
      ...endpoint,
      parameters: { ...parameters, offset, count },
    }
  }
}

interface Props {
  t: TFunction
  parsedFields: {
    endpoint: Endpoint
    visibleAccordionItems: number
  }
  parsedSitecoreContext: {
    'Discovery.Feature.Magazines.StoriesCategoryNames': SitecoreTranslations
  }
}

const MagazinesArchiveBrowserModule: React.FC<Props> = ({
  parsedFields: { endpoint, visibleAccordionItems },
  t,
  parsedSitecoreContext,
}) => {
  const isInitial = useRef(true)
  const [selectedFilters, parsedFilters, setSelectedFilters] = useSearchFilters(
    endpoint?.parameters?.aggregations.split(',')
  )

  const memoizedEndpoint: Endpoint = useMemo(
    () => ({ ...endpoint, parameters: { ...endpoint.parameters, ...parsedFilters } }),
    [endpoint, parsedFilters]
  )
  const { data, isLoading, error, size, setSize } = useSWRInfinite(getSWRKey(memoizedEndpoint), fetcher)

  const [dataInfo, setDataInfo] = React.useState({
    resultInfo: data?.[data?.length - 1]?.resultInfo,
    filterGroups: data?.[data?.length - 1]?.filterGroups,
  })

  useEffect(() => {
    if (data) {
      setDataInfo({
        resultInfo: data?.[data?.length - 1]?.resultInfo,
        filterGroups: data?.[data?.length - 1]?.filterGroups,
      })
      if (size > 1) {
        const scrollableElement = document.getElementById(`${data[size - 1]?.results[0]?.id}`)
        if (scrollableElement) {
          scrollableElement?.querySelector('a')?.focus()
        }
      }
    }
  }, [data])

  const results = data ? data.flatMap((data) => data?.results).filter((result): result is Result => !!result) : []

  const handleClearAllClick = useCallback(() => {
    setSelectedFilters([])
  }, [])

  const handleFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, selected: boolean): void => {
      const { value } = event.target as HTMLInputElement
      const [category, id] = value.split('|')

      let newFilters: SelectedFilter[]

      if (selected) {
        const newFilter = { id, category }
        newFilters = [...selectedFilters, newFilter]
        track({
          event: TrackingEvents.FILTER,
          data: {
            search: {
              filter: category.replace(/([A-Z])/g, ' $1').toLowerCase(),
            },
          },
        })
      } else {
        newFilters = selectedFilters.filter((filter) => filter.id !== id)
      }

      setSelectedFilters(newFilters)
    },
    [selectedFilters]
  )

  const onFilterTrayToggle = (e: React.MouseEvent | KeyboardEvent, isOpen: boolean): void => {
    track({
      event: TrackingEvents.INTERACTION,
      data: {
        interaction: {
          type: `filter tray:${isOpen ? 'open' : 'close'}`,
          location: t('feature-discovery-magazines-archive-analytics-location-filter', {
            defaultValue: 'auction-drawer',
          }),
        },
      },
    })
  }

  const getTotalItemsCount = (): number => {
    const totalRecords = dataInfo?.resultInfo?.totalRecords ?? 0
    const maxRecords = Number(endpoint?.parameters?.aggMax || 0)
    return totalRecords < maxRecords ? totalRecords : maxRecords
  }

  const componentStatus = useMemo(() => {
    if (isInitial.current) {
      isInitial.current = false
      return 'initial'
    }
    if (isLoading && !data?.length) {
      return 'loading'
    }
    if (size > 0 && data && typeof data[size - 1] === 'undefined') {
      return 'loadingMore'
    }
    if (error) {
      return 'error'
    }
    return 'ready'
  }, [isInitial, isLoading, size, data, error])

  const getCategoryLabel = <T extends keyof SitecoreTranslations>(
    category: T,
    sitecoreTranslations: SitecoreTranslations
  ): string => sitecoreTranslations[category]

  const accordionProps = React.useMemo(
    () => ({
      items: dataInfo?.filterGroups?.map(({ filters, title: category, id: categoryId }) => {
        const children: React.ReactNode[] = filters.map(({ text, count, id }) => {
          const textWithCount = `${text} (${count})`

          return (
            <Checkbox
              onChange={handleFilterChange}
              key={id}
              name={text}
              text={textWithCount}
              checked={!!selectedFilters?.find(({ id: selectedId }) => selectedId === id)}
              value={`${category}|${id}`}
            />
          )
        })

        return {
          id: categoryId,
          title: getCategoryLabel(
            category.toLowerCase() as keyof SitecoreTranslations,
            parsedSitecoreContext?.['Discovery.Feature.Magazines.StoriesCategoryNames']
          ),
          expanded: !!selectedFilters?.find(({ category }) => category === categoryId),
          children: (
            <>
              {children?.slice(0, visibleAccordionItems)}
              {children?.length > visibleAccordionItems && (
                <ShowMore
                  buttonVariant="inline"
                  t={{
                    showMoreLabel: t('feature-discovery-magazines-archive-filter-showmorelabel', {
                      defaultValue: 'Show more',
                    }),
                    showLessLabel: t('feature-discovery-magazines-archive-filter-showlesslabel', {
                      defaultValue: 'Show less',
                    }),
                  }}
                  focusTargetSelector={`input[value='${
                    (children[visibleAccordionItems] as React.ReactElement)?.props?.value
                  }']`}
                >
                  {children?.slice(visibleAccordionItems)}
                </ShowMore>
              )}
            </>
          ),
        }
      }),
      expandMultiple: true,
    }),
    [dataInfo, selectedFilters, handleFilterChange]
  )

  const items = React.useMemo(
    () =>
      results.flatMap((item) => ({
        id: item.id,
        children: <GenericTile {...item} />,
      })),
    [results]
  )

  const tags = React.useMemo(() => {
    return selectedFilters
      .map(({ id, category }) => {
        const filterGroup = dataInfo?.filterGroups?.find(({ id: filterGroupId }) => filterGroupId === category)?.filters
        const filterText = filterGroup?.find(({ id: filterId }) => filterId === id)?.text

        if (!filterText) return null

        return {
          id,
          children: (
            <FilterTag
              value={`${category}|${id}`}
              key={id}
              onClick={(e): void => handleFilterChange(e, false)}
              dataQa="archive-filter-tag"
              accessibilityLabel={t('feature-discovery-magazines-archive-filter-filtertagarialabel', {
                defaultValue: 'remove filter',
              })}
            >
              {filterText}
            </FilterTag>
          ),
        }
      })
      .filter((item) => item !== null) as ReactComponent[]
  }, [dataInfo, selectedFilters, handleFilterChange])

  const translations = {
    loadMoreShowLabel: t('feature-discovery-magazines-archive-filter-loadmoreshowlabel'),
    loadMoreLoadingLabel: t('feature-discovery-magazines-archive-filter-loadmoreloadinglabel'),
    loadMoreLabel: t('feature-discovery-magazines-archive-filter-loadmorelabel'),
    filterBarButtonLabel: t('feature-discovery-magazines-archive-filter-filterbarbuttonlabel'),
    filterBarBadgeAriaLabel: t('feature-discovery-magazines-archive-filter-filterbarbadgearialabel'),
    drawerAriaLabel: t('feature-discovery-magazines-archive-filter-drawerarialabel'),
    drawerCloseAriaLabel: t('feature-discovery-magazines-archive-filter-drawerclosearialabel'),
    filterTrayAriaLabel: t('feature-discovery-magazines-archive-filter-filtertrayarialabel'),
    errorLabel: t('feature-discovery-magazines-archive-filter-errorlabel'),
    filterTrayApplyLabel: t('feature-discovery-magazines-archive-filter-filtertrayapplylabel'),
    clearAllLabel: t('feature-discovery-magazines-archive-filter-clearalllabel'),
    filterTrayTitleLabel: t('feature-discovery-magazines-archive-filter-filtertraytitlelabel'),
    filterTrayClearAllLabel: t('feature-discovery-magazines-archive-filter-filtertrayclearalllabel'),
    clearAllFiltersLabel: t('feature-discovery-magazines-archive-filter-filtertraytitlelabel'),
  }

  const filterCount = selectedFilters.length

  return (
    <StoriesArchive
      isInitiallyOpen={!!filterCount}
      filterBarProps={{ selectedFilterCount: filterCount }}
      drawerProps={{
        drawerRootId: 'drawer-root',
        forceEmergeFromBottomOnMobile: true,
      }}
      accordionProps={accordionProps}
      componentStatus={componentStatus}
      items={items}
      tags={tags}
      t={translations}
      onFilterTrayToggle={onFilterTrayToggle}
      onClearAllClick={handleClearAllClick}
      error={<Error>{translations.errorLabel}</Error>}
      loadMore={
        <LoadMore
          totalItemsCount={getTotalItemsCount()}
          t={translations}
          displayedItemsCount={results.length}
          isLoading={componentStatus === 'loadingMore'}
          onLoadMore={(): void => {
            setSize(size + 1)
            track({
              event: TrackingEvents.INTERACTION,
              data: {
                interaction: {
                  type: 'load more',
                  location: t('feature-discovery-magazines-archive-analytics-location', {
                    defaultValue: 'stories:all features',
                  }),
                },
              },
            })
          }}
        />
      }
    />
  )
}

export default withParsedContext(MagazinesArchiveBrowserModule as React.FC)
